"use client";

import React from "react";
import { Cpu, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VisualAlchemy() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="147567" id="about-hercules-and-our-mission" className="bg-[#e0f2f2] text-[#222222] relative overflow-hidden py-24 lg:py-32 z-0">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none z-0 mix-blend-multiply" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} />

      {}
      <div className="absolute -top-[20%] left-[10%] w-[40%] h-[140%] bg-gradient-to-b from-[#fc6e51]/10 via-[#fc6e51]/5 to-transparent rotate-[15deg] blur-[80px] pointer-events-none z-0" />

      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#fc6e51]/5 blur-[120px] rounded-full pointer-events-none z-0" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={leftRef} className={`relative transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            {}
            <div className="absolute -inset-1 bg-gradient-to-tr from-[#ea0f0f]/20 to-[#fc6e51]/20 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition duration-700" />
            
            <Card className="relative bg-white/80 border-[#ea0f0f]/10 backdrop-blur-md overflow-hidden shadow-xl shadow-[#ea0f0f]/5 group">
              <div className="absolute inset-0 bg-gradient-to-t from-[#e0f2f2]/40 via-transparent to-transparent z-10 pointer-events-none" />
              <CardContent className="p-0">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597246/img/robot_laptops.png" alt="Robot interacting with multiple laptops" className="w-full h-auto object-cover aspect-[4/5] lg:aspect-square transition-all duration-700 scale-105 group-hover:scale-100" />
                {}
                <div className="absolute bottom-6 left-6 right-6 z-20 flex justify-between items-end">
                  <div className="space-y-2">
                    <div className="h-1 w-12 bg-[#ea0f0f] rounded-full shadow-[0_0_10px_rgba(234,15,15,0.3)]" />
                    <p className="text-xs font-mono text-[#ea0f0f]/80 tracking-widest uppercase">System Active</p>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="w-1.5 h-6 bg-[#ea0f0f]/30 rounded-sm animate-pulse" style={{
                    animationDelay: `${i * 150}ms`
                  }} />)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div ref={rightRef} className={`flex flex-col items-start transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <Badge variant="outline" className="mb-6 border-[#ea0f0f]/30 text-[#ea0f0f] bg-[#ea0f0f]/5 backdrop-blur-sm px-4 py-1.5 text-xs tracking-widest uppercase">
              <Cpu className="w-3.5 h-3.5 mr-2" />
              Our Mission
            </Badge>

            <h2 className="text-sm font-semibold text-[#ea0f0f] tracking-[0.3em] uppercase mb-4">
              MEET HERCULES
            </h2>

            <h3 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold text-[#ea0f0f] leading-tight mb-6">
              About us <br />
              <span className="text-[#fc6e51]">
                and our vision
              </span>
            </h3>

            <p className="text-lg text-[#222222] leading-relaxed mb-8 max-w-xl">
              Our passion lies in crafting insightful and engaging content that helps you explore and better understand the world of artificial intelligence. We explore AI basics in an accessible format for everyone.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-[#fc6e51] text-white hover:bg-[#ea0f0f] transition-colors duration-300 rounded-sm">
                <Link href="/HomePage">
                  Begin Here
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-[#ea0f0f] text-[#ea0f0f] hover:bg-[#ea0f0f]/5 backdrop-blur-sm rounded-sm">
                <Link href="#">
                  Contact Us
                </Link>
              </Button>
            </div>
            
            {}
            <div className="mt-12 grid grid-cols-2 gap-8 border-t border-[#ea0f0f]/20 pt-8 w-full max-w-md">
              <div>
                <p className="text-3xl font-bold text-[#ea0f0f] mb-1">120</p>
                <p className="text-xs text-[#222222] tracking-wider uppercase">Encyclopedic Pages</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#ea0f0f] mb-1">2026</p>
                <p className="text-xs text-[#222222] tracking-wider uppercase">Latest Update</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}